/**
 * Client-side avatar preparation: decode the picked photo, centre-crop
 * to a square, downscale, and re-encode as a small JPEG. Keeps uploads
 * tiny (the server caps at 2 MB) and sidesteps HEIC on the wire —
 * iPhones decode HEIC natively in the browser, so drawing to a canvas
 * and exporting JPEG converts it for free.
 */

const AVATAR_SIZE = 512;

export async function squareAvatarBlob(file: File): Promise<Blob> {
  const url = URL.createObjectURL(file);
  try {
    const img = await loadImage(url);
    const side = Math.min(img.naturalWidth, img.naturalHeight);
    if (side === 0) throw new Error('image has no pixels');
    const sx = (img.naturalWidth - side) / 2;
    const sy = (img.naturalHeight - side) / 2;

    const out = Math.min(side, AVATAR_SIZE);
    const canvas = document.createElement('canvas');
    canvas.width = out;
    canvas.height = out;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('canvas unavailable');
    ctx.drawImage(img, sx, sy, side, side, 0, 0, out, out);

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', 0.85),
    );
    if (!blob) throw new Error('could not encode image');
    return blob;
  } finally {
    URL.revokeObjectURL(url);
  }
}

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('could not read image'));
    img.src = url;
  });
}
